import React, { Suspense } from 'react';
import {
  topicTreeFPTIs,
  communityFPTIs,
  extendedFooterFPTIs,
  personalizedWidgetFPTIs,
  searchBarFPTIs,
} from '../../../helpers/analytics/homePageAnalytics';
import ErrorBoundary from '../../../shared/ErrorBoundary';
import { pageNames } from '../../../common/constants';
import { isCommunityEnabled, isLoggedIn, isNewExpLayoutEnabled } from '../../../helpers/twHelper';
import {
  layoutSize,
  isPersonalizedComponentEnabled,
  isBNAComponentEnabled,
  isQualtricsFeedbackEnabled,
} from '../../../helpers/globalHelper';

// Lazy loading components
const HeroPanel = React.lazy(() => import(/* webpackChunkName: "HeroPanel.sh.chunk" */ '../panels/HeroPanel'));
const TopicTreePanel = React.lazy(() =>
  import(/* webpackChunkName: "TopicTreePanel.sh.chunk" */ '../panels/TopicTreePanel'),
);
const PersonalizedPanel = React.lazy(() =>
  import(/* webpackChunkName: "PersonalizedPanel.sh.chunk" */ '../panels/PersonalizedPanel'),
);
const ExperienceNavBar = React.lazy(() =>
  import(/* webpackChunkName: "ExperienceNavBar.sh.chunk" */ '../../../components/experienceNavBar/ExperienceNavBar'),
);
const RecommendationsPanel = React.lazy(() =>
  import(/* webpackChunkName: "RecommendationsPanel.sh.chunk" */ '../panels/RecommendationsPanel'),
);
const CommunityPanel = React.lazy(() =>
  import(/* webpackChunkName: "CommunityPanel.sh.chunk" */ '../panels/CommunityPanel'),
);
const ExtendedFooterPanel = React.lazy(() =>
  import(/* webpackChunkName: "ExtendedFooter.sh.chunk" */ '../panels/ExtendedFooterPanel'),
);
const BNAPanel = React.lazy(() => import(/* webpackChunkName: "BNAPanel.sh.chunk" */ '../panels/BNAPanel'));
const QualtricsFeedbackPanel = React.lazy(() =>
  import(/* webpackChunkName: "QualtricsPanel.sh.chunk" */ '../panels/QualtricsPanel'),
);

const HomePage = () => {
  const newLayoutEnabled = isNewExpLayoutEnabled();
  // New template layout using experimentation.
  const expNewLayout = () => (
    <div className="sh_home__container" data-testid="sh-home-container">
      {/* Display loader (Spinner) in center while loading */}
      <Suspense fallback={<div className="home-loader-container" />}>
        <ErrorBoundary>
          <HeroPanel FPTIData={searchBarFPTIs} size={layoutSize()} />
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={topicTreeFPTIs.topicTreeError}>
          <TopicTreePanel pageName={pageNames.HOME} FPTIData={topicTreeFPTIs} />
        </ErrorBoundary>

        <ErrorBoundary>{isBNAComponentEnabled() ? <BNAPanel /> : null}</ErrorBoundary>

        <ErrorBoundary>{!isLoggedIn() ? <ExperienceNavBar /> : null}</ErrorBoundary>

        <ErrorBoundary>
          <RecommendationsPanel />
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={communityFPTIs.communityModuleError}>
          {isCommunityEnabled() ? <CommunityPanel pageName={pageNames.HOME} FPTIData={communityFPTIs} /> : null}
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={extendedFooterFPTIs.extendedFooterError}>
          <ExtendedFooterPanel pageName={pageNames.HOME} FPTIData={extendedFooterFPTIs} />
        </ErrorBoundary>

        <ErrorBoundary>
          {isQualtricsFeedbackEnabled(pageNames.HOME) ? <QualtricsFeedbackPanel pageName={pageNames.HOME} /> : null}
        </ErrorBoundary>
      </Suspense>
    </div>
  );

  // Default layout which is a new home design.
  const defaultLayout = () => (
    <div className="sh_home__container" data-testid="sh-home-container">
      {/* Display loader (Spinner) in center while loading */}
      <Suspense fallback={<div className="home-loader-container" />}>
        <ErrorBoundary>
          <HeroPanel FPTIData={searchBarFPTIs} size={layoutSize()} />
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={personalizedWidgetFPTIs.personalizedWidgetError}>
          {isPersonalizedComponentEnabled() ? <PersonalizedPanel FPTIData={personalizedWidgetFPTIs} /> : null}
        </ErrorBoundary>

        <ErrorBoundary>{isBNAComponentEnabled() ? <BNAPanel /> : null}</ErrorBoundary>

        <ErrorBoundary>{!isLoggedIn() ? <ExperienceNavBar /> : null}</ErrorBoundary>

        <ErrorBoundary>
          <RecommendationsPanel />
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={topicTreeFPTIs.topicTreeError}>
          <TopicTreePanel pageName={pageNames.HOME} FPTIData={topicTreeFPTIs} />
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={communityFPTIs.communityModuleError}>
          {isCommunityEnabled() ? <CommunityPanel pageName={pageNames.HOME} FPTIData={communityFPTIs} /> : null}
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={extendedFooterFPTIs.extendedFooterError}>
          <ExtendedFooterPanel pageName={pageNames.HOME} FPTIData={extendedFooterFPTIs} />
        </ErrorBoundary>

        <ErrorBoundary>
          {isQualtricsFeedbackEnabled(pageNames.HOME) ? <QualtricsFeedbackPanel pageName={pageNames.HOME} /> : null}
        </ErrorBoundary>
      </Suspense>
    </div>
  );

  // Render new home layout based on the experimentation.
  const renderLayout = () => {
    if (newLayoutEnabled) {
      return expNewLayout();
    }
    return defaultLayout();
  };

  return renderLayout();
};

export default HomePage;
